// Statutory test certificates ELE issues for a lift (load / hydraulic pressure / overspeed
// governor). Self-contained per-row action: a modal that lists the lift's certificates and
// generates a new one. The generated PDF is filed to Drive; the row gets a Download link. Calls
// onChanged after a successful generate so a host page (the lift detail page) can refresh.
// Extracted from AssetsPage (Session AC) so it can be reused on the detail page.
import { useMemo, useState } from "react";
import { ApiError, api } from "../../../lib/api";
import type { Asset } from "../../../lib/resources";
import type { AssetCertificateItem, CertificateType, CertificateResult } from "../../../lib/api";
import { MODAL_ACTION_BTN, MODAL_ACTIONS, ModalShell } from "../../../components/ModalShell";
import { Button } from "../../../components/Button";
import { Loader } from "../../../components/Loader";

const CERT_TYPE_OPTIONS: { value: CertificateType; label: string }[] = [
  { value: "load_test", label: "Load test" },
  { value: "hydraulic_pressure", label: "Hydraulic pressure test" },
  { value: "overspeed_governor", label: "Overspeed governor test" },
];
const CERT_RESULT_OPTIONS: { value: CertificateResult; label: string }[] = [
  { value: "pass", label: "Pass" },
  { value: "fail", label: "Fail" },
  { value: "conditional_pass", label: "Conditional pass" },
];

export function CertificatesButton({ asset, onChanged }: { asset: Asset; onChanged?: () => void }) {
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [open, setOpen] = useState(false);
  const [certs, setCerts] = useState<AssetCertificateItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [certificateType, setCertificateType] = useState<CertificateType>("load_test");
  const [testDate, setTestDate] = useState(today);
  const [result, setResult] = useState<CertificateResult>("pass");
  const [engineerName, setEngineerName] = useState("");
  const [nextTestDue, setNextTestDue] = useState("");
  const [conditions, setConditions] = useState("");

  async function openModal() {
    setOpen(true);
    setError(null);
    setLoading(true);
    try {
      setCerts(await api.assets.certificates(asset.id));
    } catch {
      setError("Could not load the certificates for this lift.");
    } finally {
      setLoading(false);
    }
  }

  async function generate() {
    if (!engineerName.trim()) {
      setError("Enter the engineer who witnessed the test.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const created = await api.assets.generateCertificate(asset.id, {
        certificateType,
        testDate: new Date(`${testDate}T00:00:00Z`).toISOString(),
        result,
        engineerName: engineerName.trim(),
        conditions: conditions.trim() || undefined,
        nextTestDue: nextTestDue ? new Date(`${nextTestDue}T00:00:00Z`).toISOString() : undefined,
      });
      setCerts((prev) => [created, ...prev]);
      setEngineerName("");
      setConditions("");
      setNextTestDue("");
      setResult("pass");
      onChanged?.();
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 503
          ? "Google Drive is not connected, so the certificate cannot be filed yet."
          : "Could not generate the certificate. Please try again in a moment.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <button
        type="button"
        onClick={openModal}
        className="rounded-ele border border-ele-blue px-3 py-1 font-menu text-xs tracking-wide text-ele-blue transition hover:bg-ele-blue/10"
      >
        Certificates
      </button>

      {open && (
        <ModalShell ariaLabel="Test certificates" onClose={() => !busy && setOpen(false)} width="max-w-lg" panelClassName="max-h-[90vh] overflow-y-auto p-6 text-left">
            <h2 className="mb-1 font-heading text-xl text-ele-strong">Test certificates</h2>
            <p className="mb-4 text-sm text-ele-body">
              {asset.eleNumber ?? "Lift"}
              {asset.site?.name ? `, ${asset.site.name}` : ""}
            </p>

            <h3 className="mb-2 font-menu text-xs uppercase tracking-wider text-ele-blue">On file</h3>
            {loading ? (
              <Loader shape="inline" />
            ) : certs.length === 0 ? (
              <p className="text-sm text-ele-body opacity-70">No test certificates yet.</p>
            ) : (
              <ul className="mb-4 space-y-2">
                {certs.map((c) => (
                  <li key={c.id} className="rounded-ele border border-ele-purple-accent bg-ele-surface px-3 py-2 text-sm">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-ele-strong">{c.certNumber}</span>
                      <a
                        href={api.assets.certificateDownloadHref(asset.id, c.id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-ele-green hover:underline"
                      >
                        Download
                      </a>
                    </div>
                    <div className="text-ele-body">
                      {c.type}, {c.result}, tested {c.testDateDisplay}
                      {c.nextTestDueDisplay ? `, next due ${c.nextTestDueDisplay}` : ""}
                    </div>
                  </li>
                ))}
              </ul>
            )}

            <h3 className="mb-2 mt-4 font-menu text-xs uppercase tracking-wider text-ele-blue">Generate a certificate</h3>
            <div className="space-y-3">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm text-ele-body" htmlFor="cert-type">Type</label>
                  <select
                    id="cert-type"
                    value={certificateType}
                    onChange={(e) => setCertificateType(e.target.value as CertificateType)}
                    className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green"
                  >
                    {CERT_TYPE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm text-ele-body" htmlFor="cert-result">Result</label>
                  <select
                    id="cert-result"
                    value={result}
                    onChange={(e) => setResult(e.target.value as CertificateResult)}
                    className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green"
                  >
                    {CERT_RESULT_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm text-ele-body" htmlFor="cert-testdate">Test date</label>
                  <input
                    id="cert-testdate"
                    type="date"
                    value={testDate}
                    onChange={(e) => setTestDate(e.target.value)}
                    className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm text-ele-body" htmlFor="cert-nextdue">Next test due (optional)</label>
                  <input
                    id="cert-nextdue"
                    type="date"
                    value={nextTestDue}
                    onChange={(e) => setNextTestDue(e.target.value)}
                    className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green"
                  />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm text-ele-body" htmlFor="cert-engineer">Engineer who witnessed the test</label>
                <input
                  id="cert-engineer"
                  type="text"
                  value={engineerName}
                  onChange={(e) => setEngineerName(e.target.value)}
                  className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm text-ele-body" htmlFor="cert-conditions">Conditions or observations (optional)</label>
                <textarea
                  id="cert-conditions"
                  value={conditions}
                  onChange={(e) => setConditions(e.target.value)}
                  rows={2}
                  className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green"
                />
              </div>
              {error && <p role="alert" className="text-sm text-ele-danger">{error}</p>}
            </div>

            <div className={`mt-4 gap-2 ${MODAL_ACTIONS}`}>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body ${MODAL_ACTION_BTN}`}
              >
                Close
              </button>
              <Button size="sm" className={MODAL_ACTION_BTN} onClick={generate} disabled={busy || !engineerName.trim()}>
                {busy ? "Generating..." : "Generate"}
              </Button>
            </div>
        </ModalShell>
      )}
    </div>
  );
}
